/**
 * 認証サービス
 * Cognito Hosted UIを使用した認証処理
 */

import { getLoginUrl, getLogoutUrl, cognitoConfig } from "@/config/cognito";

const TOKEN_KEY = "backyard_auth_token";
const ID_TOKEN_KEY = "backyard_id_token";
const REFRESH_TOKEN_KEY = "backyard_refresh_token";
const USER_INFO_KEY = "backyard_user_info";

export interface UserInfo {
  email: string;
  sub: string; // Cognito User ID
  name?: string;
  profile?: string; // ユーザーのロール（member/staff/admin）
  "cognito:groups"?: string[]; // ユーザーが属するグループ
}

/**
 * ログイン処理（Hosted UIへリダイレクト）
 */
export function login(): void {
  window.location.href = getLoginUrl();
}

/**
 * ログアウト処理
 */
export function logout(): void {
  // ローカルのトークンをクリア
  clearTokens();

  // Cognitoのログアウトページへリダイレクト
  window.location.href = getLogoutUrl();
}

/**
 * 認証コードからトークンを取得
 */
export async function handleAuthCallback(code: string): Promise<void> {
  try {
    const params = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: cognitoConfig.clientId,
      code: code,
      redirect_uri: cognitoConfig.redirectUri,
    });

    const response = await fetch(`${cognitoConfig.domain}/oauth2/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error("トークンの取得に失敗しました");
    }

    const data = await response.json();

    // トークンを保存
    saveTokens(data.access_token, data.id_token, data.refresh_token);

    // ユーザー情報を取得して保存
    await fetchAndSaveUserInfo(data.access_token);
  } catch (error) {
    console.error("認証エラー:", error);
    throw error;
  }
}

/**
 * ユーザー情報を取得
 */
async function fetchAndSaveUserInfo(accessToken: string): Promise<void> {
  try {
    const response = await fetch(`${cognitoConfig.domain}/oauth2/userInfo`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("ユーザー情報の取得に失敗しました");
    }

    const userInfo = await response.json();
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  } catch (error) {
    console.error("ユーザー情報取得エラー:", error);
    throw error;
  }
}

/**
 * トークンを保存
 */
function saveTokens(
  accessToken: string,
  idToken: string,
  refreshToken: string
): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(ID_TOKEN_KEY, idToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * トークンをクリア
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_INFO_KEY);
}

/**
 * アクセストークンを取得
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * IDトークンを取得
 */
export function getIdToken(): string | null {
  return localStorage.getItem(ID_TOKEN_KEY);
}

/**
 * ユーザー情報を取得
 */
export function getUserInfo(): UserInfo | null {
  const userInfoStr = localStorage.getItem(USER_INFO_KEY);
  if (!userInfoStr) return null;

  try {
    return JSON.parse(userInfoStr) as UserInfo;
  } catch {
    return null;
  }
}

/**
 * トークンの有効期限をチェック
 */
export function isTokenValid(): boolean {
  const idToken = getIdToken();
  if (!idToken) return false;

  try {
    // JWTトークンをデコード
    const payload = JSON.parse(atob(idToken.split(".")[1]));
    const exp = payload.exp * 1000; // 秒をミリ秒に変換
    const now = Date.now();

    // トークンの有効期限をチェック（5分前に期限切れとみなす）
    return exp > now + 5 * 60 * 1000;
  } catch (error) {
    console.error("Token validation error:", error);
    return false;
  }
}

/**
 * 認証済みかチェック
 */
export function isAuthenticated(): boolean {
  return !!getAccessToken() && isTokenValid();
}

/**
 * ユーザーが特定のグループに属しているかチェック
 */
export function isInGroup(groupName: string): boolean {
  const userInfo = getUserInfo();
  if (!userInfo || !userInfo["cognito:groups"]) {
    return false;
  }
  return userInfo["cognito:groups"].includes(groupName);
}

/**
 * ユーザーの役割を取得
 */
export function getUserRole(): "admin" | "staff" | "member" | null {
  const userInfo = getUserInfo();
  if (!userInfo) return null;

  // 1. まず profile フィールドをチェック（Cognitoの属性として設定されている場合）
  if (userInfo.profile) {
    const role = userInfo.profile.toLowerCase();
    if (role === "admin" || role === "staff" || role === "member") {
      return role as "admin" | "staff" | "member";
    }
  }

  // 2. cognito:groups をチェック（Lambda トリガーで設定されている場合）
  const groups = userInfo["cognito:groups"] || [];

  // 優先順位: admin > staff > member
  if (groups.includes("admin")) return "admin";
  if (groups.includes("staff")) return "staff";
  if (groups.includes("member")) return "member";

  // どのロールも設定されていない場合はnull
  return null;
}

/**
 * トークンをリフレッシュ
 */
export async function refreshToken(): Promise<void> {
  const refresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refresh) {
    throw new Error("リフレッシュトークンがありません");
  }

  try {
    const params = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: cognitoConfig.clientId,
      refresh_token: refresh,
    });

    const response = await fetch(`${cognitoConfig.domain}/oauth2/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error("トークンのリフレッシュに失敗しました");
    }

    const data = await response.json();

    // 新しいトークンを保存（リフレッシュトークンは含まれない場合があるため既存のものを保持）
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(ID_TOKEN_KEY, data.id_token);
  } catch (error) {
    console.error("トークンリフレッシュエラー:", error);
    // リフレッシュ失敗時はログアウト
    clearTokens();
    throw error;
  }
}

export default {
  login,
  logout,
  handleAuthCallback,
  getAccessToken,
  getIdToken,
  getUserInfo,
  isAuthenticated,
  isInGroup,
  getUserRole,
  refreshToken,
};
